import React, {useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useForm} from 'react-hook-form'
import {Button, VStack} from '@chakra-ui/react'
import usePressContactFormFields from '../../../components/forms/usePressContactFormFields'
import Field from '../../../components/field'
import Recaptcha from '../../../components/recaptcha'
import {API_ERROR_MESSAGE} from '../../../constants'
import {useCustomerServicePressContactMutation} from '../../../hooks/useScapiCustomApi'
import {useToast} from '../../../hooks/use-toast'
import useCustomerServicePageConfig from '../hooks/use-page-config'
import CustomerServiceLayoutDefault from '../layout/default'
import CSHeader from '../partials/header'
import PropTypes from 'prop-types'

const CustomerServicePressContactFormPage = ({countries}) => {
    const {formatMessage} = useIntl()
    const form = useForm()
    const toast = useToast()
    const recaptchaRef = useRef(null)
    const {name, description} = useCustomerServicePageConfig()
    const fields = usePressContactFormFields({form, prefix: '', countries})

    // until commerce-sdk-react@3.10.0 we can't provide the apiOptions to the mutateAsync function
    // and need to use a state and effect to submit the form
    const [apiOptions, setApiOptions] = useState(null)
    const {mutateAsync: postContactForm, isLoading: isPostContactFormLoading} =
        useCustomerServicePressContactMutation(apiOptions, {
            onError: (error) => {
                console.error('Error while sending contact form', error)
                toast({
                    title: formatMessage(API_ERROR_MESSAGE),
                    status: 'error'
                })
            },
            onSuccess: () => {
                toast({
                    title: formatMessage({
                        defaultMessage: 'The contact form has been sent successfully.',
                        id: 'customer_service.contactform.toast.success'
                    }),
                    status: 'success',
                    duration: 10000
                })
                form.reset()
            }
        })
    useEffect(() => {
        if (apiOptions) {
            postContactForm(apiOptions)
            setApiOptions(null)
        }
    }, [apiOptions, postContactForm])

    const submitForm = async (data) => {
        const token = await recaptchaRef.current.execute()
        const body = {
            country: data.country,
            email: data.email,
            subject: data.subject,
            message: data.message
        }
        const parameters = {
            c_gRecaptchaResponse: token
        }

        // this will submit the form on the next render
        setApiOptions({body, parameters})
    }

    // compute a combined loading state for when the form is submitting
    const isLoading =
        // if we are in the submitForm handler
        form.formState.isSubmitting ||
        // if we have a pending api call
        !!apiOptions ||
        // if we are waiting for the api call to finish
        isPostContactFormLoading

    return (
        <CustomerServiceLayoutDefault>
            <CSHeader headline={name} subline={description} />
            <VStack
                width={{base: '90%', md: '50%'}}
                as="form"
                padding={4}
                gap={4}
                onSubmit={form.handleSubmit(submitForm)}
            >
                <Field {...fields.country} />
                <Field {...fields.email} />
                <Field {...fields.subject} />
                <Field {...fields.message} />
                <Recaptcha ref={recaptchaRef} />
                <Button
                    type="submit"
                    width="full"
                    onClick={() => {
                        form.clearErrors('global')
                        // todo trackEvent
                    }}
                    isLoading={isLoading}
                >
                    {formatMessage({
                        defaultMessage: 'Submit',
                        id: 'global.submit'
                    })}
                </Button>
            </VStack>
        </CustomerServiceLayoutDefault>
    )
}

CustomerServicePressContactFormPage.propTypes = {
    countries: PropTypes.array.isRequired
}

export default CustomerServicePressContactFormPage
